/**
 * Prune-only localStorage maintenance for the drafts store (sgdev card `8e1`,
 * entry `e17`).
 *
 * IndexedDB is the primary draft store, and new draft content goes there and
 * nowhere else — the bake-period localStorage mirror is gone. But the legacy
 * `localStorage['autosaves']` key cannot simply be abandoned, because the
 * provider re-reads it in its `useState` initializers on EVERY mount, forever,
 * and `mergeAutosaves` is a union by id that never subtracts. A frozen
 * localStorage snapshot therefore resurrects drafts: a draft the caseworker
 * permanently deleted more than 30 days ago has had its IndexedDB tombstone
 * purged, so nothing outranks the frozen copy and it reappears — carrying its
 * PHI — in the drafts list, and is then written back to IndexedDB.
 *
 * The fix is to keep localStorage correct for DELETIONS while it stops
 * receiving CONTENT:
 *
 *   - never add an entry, never update an entry's content;
 *   - drop any entry IndexedDB now holds an equal-or-newer copy of (that is
 *     every deleted, trashed, and edited draft — anything that could not win
 *     the merge anyway), plus any entry IndexedDB no longer holds at all
 *     (a tombstone that has since been purged, which is the resurrection
 *     case itself);
 *   - when nothing is left, `removeItem` the key.
 *
 * Old drafts therefore drain piecemeal as the caseworker works, and the empty
 * key is the drain's own stop condition — there is no migration flag and no
 * batch migration to write. The migration has been running since 2026-07-23:
 * every load merges localStorage into state and the persist effect writes it
 * to IndexedDB. All that was missing was a way for it to stop.
 *
 * This binds IndexedDB mode ONLY. In the localStorage fallback (no IDB
 * available) localStorage is legitimately additive — that is the whole point
 * of keeping that branch.
 */

import { AutosaveT } from './AutosaveContext';

/** The legacy drafts key. Read by the provider's state initializers on mount. */
export const LOCAL_STORAGE_AUTOSAVES_KEY = 'autosaves';

export type LocalStoragePruneOutcome =
  /** Nothing to do — no key, nothing parseable, or nothing prunable. */
  | { action: 'none'; prunedIds: [] }
  /** The array emptied: remove the key so the drain has ended. */
  | { action: 'remove'; prunedIds: string[] }
  /** A strictly smaller array, in the shape it was stored in. */
  | { action: 'write'; serialized: string; prunedIds: string[] };

const NOTHING_TO_DO: LocalStoragePruneOutcome = { action: 'none', prunedIds: [] };

/**
 * Mirrors `getEffectiveUpdateTime` in AutosaveContext: a trash or a permanent
 * delete is an update, so a tombstone outranks the pre-trash copy.
 */
const effectiveUpdateTime = (record: AutosaveT): number =>
  Math.max(
    record.timeUpdated || 0,
    record.trashedAt || 0,
    record.permanentlyDeletedAt || 0
  );

/**
 * Decide what (if anything) to write back over the stored drafts array.
 *
 * Pure, so the prune rule is testable without touching storage. `current` is
 * the state that was JUST persisted to IndexedDB successfully — pruning
 * against anything less than a committed write could drop the only copy of a
 * legacy draft.
 */
export const computeLocalStoragePrune = (
  rawStored: string | null,
  current: AutosaveT[]
): LocalStoragePruneOutcome => {
  if (!rawStored) return NOTHING_TO_DO;

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawStored);
  } catch {
    // Unreadable. The provider's initializers already treat it as no drafts,
    // so it holds nothing recoverable — but removing a value we cannot read
    // is not a prune, so leave it exactly as found.
    return NOTHING_TO_DO;
  }

  const storedIsLegacyArray = Array.isArray(parsed);
  const storedRecords: AutosaveT[] = storedIsLegacyArray
    ? (parsed as AutosaveT[])
    : Array.isArray((parsed as { autosaves?: unknown })?.autosaves)
      ? ((parsed as { autosaves: AutosaveT[] }).autosaves)
      : [];

  const currentById = new Map<string, AutosaveT>();
  current.forEach((record) => currentById.set(record.id, record));

  const prunedIds: string[] = [];
  const kept = storedRecords.filter((record) => {
    const held = currentById.get(record.id);
    // Not in IndexedDB at all: a tombstone that has since been purged (the
    // resurrection case), so this copy is the stale one.
    // Held with an equal-or-newer effective update time: this copy can never
    // win the merge, so it is pure dead weight.
    const prunable = !held || effectiveUpdateTime(held) >= effectiveUpdateTime(record);
    if (prunable && record?.id) prunedIds.push(record.id);
    return !prunable;
  });

  if (kept.length === 0) {
    // Includes the already-empty case: removing the key is what ends the
    // drain, rather than leaving an empty husk to re-parse on every mount.
    return { action: 'remove', prunedIds };
  }

  if (prunedIds.length === 0) return NOTHING_TO_DO;

  // Write back in the shape it was stored in — re-wrapping a legacy array in
  // the object form would GROW the value, and a prune must only ever shrink.
  const next = storedIsLegacyArray
    ? kept
    : { ...(parsed as Record<string, unknown>), autosaves: kept };

  return { action: 'write', serialized: JSON.stringify(next), prunedIds };
};

/**
 * Apply `computeLocalStoragePrune` to real storage. Call ONLY from IndexedDB
 * mode, and only after the IndexedDB write for `current` has committed.
 *
 * Returns the ids it removed (empty when it did nothing) so callers can log or
 * assert on the drain. A quota failure here is harmless and swallowed: the
 * intent is monotonically shrinking, so the next delete retries it.
 */
export const pruneLocalStorageAutosaves = (current: AutosaveT[]): string[] => {
  let outcome: LocalStoragePruneOutcome;
  try {
    outcome = computeLocalStoragePrune(
      localStorage.getItem(LOCAL_STORAGE_AUTOSAVES_KEY),
      current
    );
  } catch {
    return [];
  }

  try {
    if (outcome.action === 'remove') {
      localStorage.removeItem(LOCAL_STORAGE_AUTOSAVES_KEY);
    } else if (outcome.action === 'write') {
      localStorage.setItem(LOCAL_STORAGE_AUTOSAVES_KEY, outcome.serialized);
    }
  } catch {
    /* shrinking writes retry on the next change */
  }

  return outcome.prunedIds;
};
